import React, {FC, useEffect} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {CancelToken} from '../../../../../../../../src/helpers/asyncCancelHelpers';
import {ResolveDispatchProps} from '../../../../../../../../src/helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../src/helpers/reselectHelpers';
import {
  makeGetContactsManagerParameters,
  makeGetContactsManagerSearchState,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {contactsManagerSearchTablesForceReloadedAction} from '../../../../../../../../src/store/panels/contacts/contactsManager/search/contactsManagerSearchActions';
import {
  contactsManagerFetchSearchCardGroupsTableDataAsyncAction,
  contactsManagerSetUpSearchCardGroupsTableAction,
  contactsManagerTableAddSelectedCardGroupIdsAction,
  contactsManagerTableRemoveSelectedCardGroupIdsAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../../../../../src/store/stateless/navigationActions';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {CardGroupsSearchTable} from './cardGroupsSearchTable';

/*
 * Props.
 */

interface CardGroupsTableContainerPublicProps {
  panelId: ContactsManagerPanelId;
  renderEmptyState: Renderer;
}

type CardGroupsTableContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type CardGroupsTableContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type CardGroupsTableContainerProps = CardGroupsTableContainerPublicProps &
  CardGroupsTableContainerStateProps &
  CardGroupsTableContainerDispatchProps;

/*
 * Selectors.
 */

function makeGetQuery() {
  return createSelector([makeGetContactsManagerParameters()], (params) => {
    if (params.type !== ContactsManagerParametersTypesEnum.SEARCH) {
      return '';
    }
    return params.query;
  });
}

function makeGetCardGroupIds(
  getContactsManagerSearchState: ReturnType<typeof makeGetContactsManagerSearchState>,
) {
  return createSelector(
    [getContactsManagerSearchState],
    (searchState) => searchState?.cardGroupsTableState?.cardGroupIds ?? [],
  );
}

function makeGetSelectedCardGroupIds(
  getContactsManagerSearchState: ReturnType<typeof makeGetContactsManagerSearchState>,
) {
  return createSelector(
    [getContactsManagerSearchState],
    (searchState) => searchState?.cardGroupsTableState?.selectedCardGroupIds ?? [],
  );
}

function makeGetPaginationData(
  getContactsManagerSearchState: ReturnType<typeof makeGetContactsManagerSearchState>,
) {
  return createSelector(
    [getContactsManagerSearchState],
    (searchState) => searchState?.cardGroupsTableState?.paginationData,
  );
}

function makeGetIsInMultiSelectMode(
  getContactsManagerSearchState: ReturnType<typeof makeGetContactsManagerSearchState>,
) {
  return createSelector(
    [getContactsManagerSearchState],
    (searchState) => searchState?.cardGroupsTableState?.isInMultiSelectMode,
  );
}

/*
 * Component.
 */

export const CardGroupsSearchTableContainerComponent: FC<CardGroupsTableContainerProps> = (props) => {
  const {
    searchState,
    cardGroupIds,
    selectedCardGroupIds,
    paginationData,
    query,
    isInMultiSelectMode,
    onFetchTableDataAsync,
    onAddSelectedCardGroupIds,
    onRemoveSelectedCardGroupIds,
    onReset,
    navigateToLink,
    setUpSearchCardGroupsTable,
    renderEmptyState,
  } = props;

  // When we change tabs, we should remove any selected card group ids.
  useInitialEffect(() => () => onRemoveSelectedCardGroupIds(selectedCardGroupIds));

  useEffect(() => {
    if (!searchState?.cardGroupsTableState) {
      setUpSearchCardGroupsTable();
    }
  }, [searchState?.cardGroupsTableState, setUpSearchCardGroupsTable]);

  return (
    <CardGroupsSearchTable
      renderEmptyState={renderEmptyState}
      cardGroupIds={cardGroupIds}
      selectedCardGroupIds={selectedCardGroupIds}
      paginationData={paginationData}
      query={query}
      isInMultiSelectMode={isInMultiSelectMode}
      onFetchTableDataAsync={onFetchTableDataAsync}
      onAddSelectedCardGroupIds={onAddSelectedCardGroupIds}
      onRemoveSelectedCardGroupIds={onRemoveSelectedCardGroupIds}
      onReset={onReset}
      navigateToLink={navigateToLink}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CardGroupsTableContainerPublicProps>()(() => {
  const getContactsManagerSearchState = makeGetContactsManagerSearchState();
  return {
    searchState: getContactsManagerSearchState,
    cardGroupIds: makeGetCardGroupIds(getContactsManagerSearchState),
    selectedCardGroupIds: makeGetSelectedCardGroupIds(getContactsManagerSearchState),
    paginationData: makeGetPaginationData(getContactsManagerSearchState),
    query: makeGetQuery(),
    isInMultiSelectMode: makeGetIsInMultiSelectMode(getContactsManagerSearchState),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<CardGroupsTableContainerPublicProps>()(({panelId}) => ({
  onFetchTableDataAsync: (query: string, cancelToken: CancelToken) =>
    contactsManagerFetchSearchCardGroupsTableDataAsyncAction(panelId, query, cancelToken),
  onAddSelectedCardGroupIds: (cardGroupIds: ReadonlyArray<number>, isInMultiSelectMode?: boolean) =>
    contactsManagerTableAddSelectedCardGroupIdsAction(panelId, cardGroupIds, isInMultiSelectMode),
  onRemoveSelectedCardGroupIds: (cardGroupIds: ReadonlyArray<number>) =>
    contactsManagerTableRemoveSelectedCardGroupIdsAction(panelId, cardGroupIds),
  onReset: () => contactsManagerSearchTablesForceReloadedAction(panelId),
  navigateToLink: navigateToLinkAction,
  setUpSearchCardGroupsTable: contactsManagerSetUpSearchCardGroupsTableAction,
}));

export const CardGroupsSearchTableContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CardGroupsSearchTableContainerComponent);
